import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { usePermission } from '../hooks/usePermission';
import * as Authservices from '../Services/services';

const flagLabels = {
    violence: { label: 'Violence', color: '#ef4444' },
    mature_content: { label: 'Mature', color: '#f59e0b' },
    inappropriate_content: { label: 'Inappropriate', color: '#dc2626' },
    spam: { label: 'Spam', color: '#8b5cf6' },
    copyright: { label: 'Copyright', color: '#0ea5e9' },
};

const priorityColors = { urgent: '#ef4444', high: '#f59e0b', normal: '#6366f1' };

const ContentModeration = () => {
    const { can } = usePermission();
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('pending');
    const [total, setTotal] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
    const [page, setPage] = useState(1);
    const limit = 20;

    const [rejectReason, setRejectReason] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectingId, setRejectingId] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);

    const fetchReports = useCallback(() => {
        setLoading(true);
        const params = { status: filter, page, limit };
        Authservices.getModerationReports(params)
            .then((res) => {
                const rd = res.data?.responseDetails || res.data?.data || {};
                const rows = rd.rows || rd.data || rd || [];
                const rowsArray = Array.isArray(rows) ? rows : [];
                setQueue(rowsArray);
                const t = rd.total || rd.recordsTotal || 0;
                setTotal(prev => ({ ...prev, [filter]: t, all: filter === 'all' ? t : prev.all }));
            })
            .catch((err) => {
                toast.error(err?.response?.data?.responseMessage || 'Failed to load moderation queue.');
            })
            .finally(() => setLoading(false));
    }, [filter, page]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleApprove = (id) => {
        if (!can('content_moderation', 'action')) {
            toast.error("You don't have permission to approve content.");
            return;
        }
        setActionLoading(id + '_approve');
        Authservices.approveModerationReport(id)
            .then(() => {
                toast.success('Content approved successfully.');
                fetchReports();
            })
            .catch((err) => {
                toast.error(err?.response?.data?.responseMessage || 'Failed to approve content.');
            })
            .finally(() => setActionLoading(null));
    };

    const openRejectModal = (id) => {
        setRejectingId(id);
        setRejectReason('');
        setShowRejectModal(true);
    };

    const handleReject = () => {
        if (!rejectReason.trim()) return;
        if (!can('content_moderation', 'action')) {
            toast.error("You don't have permission to reject content.");
            return;
        }
        setActionLoading(rejectingId + '_reject');
        Authservices.rejectModerationReport(rejectingId, rejectReason.trim())
            .then(() => {
                toast.success('Content rejected.');
                setShowRejectModal(false);
                setRejectReason('');
                setRejectingId(null);
                fetchReports();
            })
            .catch((err) => {
                toast.error(err?.response?.data?.responseMessage || 'Failed to reject content.');
            })
            .finally(() => setActionLoading(null));
    };

    const handleFilterChange = (f) => {
        setFilter(f);
        setPage(1);
    };

    const counts = total;

    return (
        <section className="moderation-section section">
            <div className="moderation-header">
                <div>
                    <h4>Content Moderation</h4>
                    <p className="moderation-subtitle">Review and approve content before it goes live</p>
                </div>
                <div className="moderation-stats-row">
                    <div className="moderation-stat">
                        <span className="moderation-stat-value" style={{ color: '#f59e0b' }}>{counts.pending}</span>
                        <span className="moderation-stat-label">Pending</span>
                    </div>
                    <div className="moderation-stat">
                        <span className="moderation-stat-value" style={{ color: '#10b981' }}>{counts.approved}</span>
                        <span className="moderation-stat-label">Approved</span>
                    </div>
                    <div className="moderation-stat">
                        <span className="moderation-stat-value" style={{ color: '#ef4444' }}>{counts.rejected}</span>
                        <span className="moderation-stat-label">Rejected</span>
                    </div>
                </div>
            </div>

            <div className="moderation-tabs">
                {['pending', 'approved', 'rejected', 'all'].map(f => (
                    <button key={f} className={`moderation-tab ${filter === f ? 'active' : ''}`} onClick={() => handleFilterChange(f)}>
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                        {counts[f] > 0 && <span className="moderation-tab-count">{counts[f]}</span>}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="moderation-empty">
                    <i className="fas fa-spinner fa-spin"></i>
                    <h5>Loading...</h5>
                </div>
            ) : (
                <div className="moderation-list">
                    {queue.map(item => (
                        <div key={item.id} className={`moderation-card ${item.status}`}>
                            <div className="moderation-card-left">
                                <div className="moderation-thumb">
                                    {item.thumbnail
                                        ? <img src={item.thumbnail} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                                        : <i className={item.type === 'series' ? 'fas fa-film' : 'fas fa-play-circle'}></i>
                                    }
                                </div>
                                <div className="moderation-card-info">
                                    <div className="moderation-card-top">
                                        <h6>{item.title}</h6>
                                        <span className={`moderation-type-badge ${item.type}`}>{item.type}</span>
                                        {item.priority && (
                                            <span className="moderation-priority" style={{ color: priorityColors[item.priority] }}>
                                                <i className="fas fa-circle" style={{ fontSize: '6px' }}></i> {item.priority}
                                            </span>
                                        )}
                                        {(item.flags || []).map(flag => (
                                            <span key={flag} className="moderation-flag" style={{ background: `${flagLabels[flag]?.color}20`, color: flagLabels[flag]?.color }}>
                                                <i className="fas fa-exclamation-triangle" style={{ fontSize: '10px' }}></i> {flagLabels[flag]?.label || flag}
                                            </span>
                                        ))}
                                    </div>
                                    {item.description && <p className="moderation-desc">{item.description}</p>}
                                    <div className="moderation-meta">
                                        {item.submitted_by && <span><i className="fas fa-user"></i> {item.submitted_by}</span>}
                                        {item.submitted_at && <span><i className="fas fa-clock"></i> {new Date(item.submitted_at).toLocaleDateString()}</span>}
                                    </div>
                                    {item.status === 'rejected' && item.reject_reason && (
                                        <div className="moderation-reject-reason">
                                            <i className="fas fa-ban"></i> <strong>Reason:</strong> {item.reject_reason}
                                        </div>
                                    )}
                                    {item.reviewed_by && (
                                        <div className="moderation-reviewed">
                                            <i className="fas fa-check-double"></i> Reviewed by <strong>{item.reviewed_by}</strong>
                                            {item.reviewed_at && <> on {new Date(item.reviewed_at).toLocaleDateString()}</>}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {item.status === 'pending' && can('content_moderation', 'action') && (
                                <div className="moderation-actions">
                                    <button
                                        className="moderation-approve-btn"
                                        disabled={actionLoading === item.id + '_approve'}
                                        onClick={() => handleApprove(item.id)}
                                    >
                                        {actionLoading === item.id + '_approve'
                                            ? <i className="fas fa-spinner fa-spin"></i>
                                            : <><i className="fas fa-check"></i> Approve</>
                                        }
                                    </button>
                                    <button
                                        className="moderation-reject-btn"
                                        disabled={actionLoading === item.id + '_reject'}
                                        onClick={() => openRejectModal(item.id)}
                                    >
                                        <i className="fas fa-times"></i> Reject
                                    </button>
                                </div>
                            )}
                            {item.status !== 'pending' && (
                                <div className="moderation-status-badge-wrap">
                                    <span className={`moderation-status-badge ${item.status}`}>
                                        <i className={`fas ${item.status === 'approved' ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                        {item.status}
                                    </span>
                                </div>
                            )}
                        </div>
                    ))}
                    {queue.length === 0 && (
                        <div className="moderation-empty">
                            <i className="fas fa-inbox"></i>
                            <h5>No items found</h5>
                            <p>There are no {filter} items in the moderation queue</p>
                        </div>
                    )}
                </div>
            )}

            {showRejectModal && (
                <div className="common-popup active" onClick={(e) => { if (e.target.classList.contains('common-popup')) setShowRejectModal(false); }}>
                    <div className="popup-content" style={{ maxWidth: '480px' }}>
                        <button className="close-btn" onClick={() => setShowRejectModal(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                        <h5 style={{ marginBottom: '8px', color: '#ef4444' }}><i className="fas fa-exclamation-triangle"></i> Reject Content</h5>
                        <p style={{ color: '#8a8a9a', marginBottom: '20px', fontSize: '14px' }}>Please provide a reason for rejecting this content. The submitter will be notified.</p>
                        <div className="form-group" style={{ marginBottom: '24px' }}>
                            <label className="form-label">Rejection Reason</label>
                            <textarea
                                className="form-control"
                                rows="4"
                                placeholder="Explain why this content is being rejected..."
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                style={{ resize: 'vertical' }}
                            ></textarea>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>Cancel</button>
                            <button
                                className="btn"
                                style={{ background: '#ef4444', color: 'white' }}
                                onClick={handleReject}
                                disabled={!rejectReason.trim() || actionLoading === rejectingId + '_reject'}
                            >
                                {actionLoading === rejectingId + '_reject'
                                    ? <><i className="fas fa-spinner fa-spin"></i> Rejecting...</>
                                    : 'Confirm Rejection'
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ContentModeration;
